import type {
  AppContentCurrentEntity,
  AppContentVersionEntity,
} from '../entities/app-content.entity';
import type { UpdateAppContentVersionDto } from '../dtos/update-app-content-version.dto';
import type { AppContentRepository } from '../repositories/app-content-repository.interface';
import { GetAppContentUseCase } from './get-app-content.usecase';

export class UpdateAppContentUseCase {
  private readonly repository: AppContentRepository;
  private readonly getAppContentUseCase: GetAppContentUseCase;

  constructor(
    repository: AppContentRepository,
    getAppContentUseCase: GetAppContentUseCase
  ) {
    this.repository = repository;
    this.getAppContentUseCase = getAppContentUseCase;
  }

  async execute(params: UpdateAppContentVersionDto): Promise<void> {
    const {
      key,
      content,
      changeNotes,
      requiresReacceptance,
      updatedByUid,
      updatedByName,
    } = params;

    const current = await this.getAppContentUseCase.executeCurrent(key);
    const now = new Date();

    if ((current?.currentContent ?? '').trim() === content.trim()) {
      throw new Error('O conteúdo não foi alterado. Não há nova versão para publicar.');
    }

    const nextVersion = (current?.currentVersion ?? 0) + 1;

    if (current && current.currentVersion > 0) {
      const oldVersionSnapshot: AppContentVersionEntity = {
        version: current.currentVersion,
        content: current.currentContent,
        changeNotes: current.lastChangeNotes ?? 'Snapshot automático da versão anterior.',
        requiresReacceptance: current.requiresReacceptance ?? true,
        createdAt: current.updatedAt ?? current.createdAt ?? now,
        createdByUid:
          current.updatedByUid ??
          current.createdByUid ??
          updatedByUid ??
          null,
        createdByName:
          current.updatedByName ??
          current.createdByName ??
          updatedByName ??
          null,
        publishedAt: current.publishedAt ?? current.updatedAt ?? now,
      };
      await this.repository.addOldVersion(key, oldVersionSnapshot);
    }

    const nextCurrent: AppContentCurrentEntity = {
      currentVersion: nextVersion,
      currentContent: content,
      lastChangeNotes: changeNotes,
      requiresReacceptance,
      createdAt: current?.createdAt ?? now,
      createdByUid: current?.createdByUid ?? updatedByUid ?? null,
      createdByName: current?.createdByName ?? updatedByName ?? null,
      updatedAt: now,
      updatedByUid: updatedByUid ?? null,
      updatedByName: updatedByName ?? null,
      publishedAt: now,
    };

    await this.repository.setCurrentVersion(key, nextCurrent);
  }
}
